/**
 * Spanish message catalog — mirrors the shape of `messages.en.ts`
 * exactly. The shape is enforced structurally via `satisfies` against
 * a widened (non-`as const`) view of the EN catalog so a new key in
 * EN that doesn't land here is a TypeScript error.
 *
 * `satisfies` (rather than `: MessagesShape =`) preserves the literal
 * Spanish strings on the exported object so future tooling — testing
 * against translation keys, generating PO files — can read the exact
 * Spanish text rather than the widened `string` type.
 *
 * Wired in `index.ts` once a runtime locale picker exists. For now
 * the file lives standalone so the EN catalog still drives all
 * runtime reads.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Conectado",
    statusReconnecting: "Reconectando…",
    statusDisconnected: "Desconectado",
    openSettings: "Abrir configuración",
    pairedFallbackDevice: "tu teléfono",
    publicModeToggleEnable: "Ocultar contenido de notificaciones",
    publicModeToggleDisable: "Mostrar contenido de notificaciones",
  },

  notifications: {
    replyAriaLabel: "Responder",
    replyPlaceholder: "Responder…",
    replySend: "Enviar",
    sent: "Enviado",
    failedToSend: "No se pudo enviar",
    unread: "Sin leer",
    dismiss: "Descartar",
    notificationImageAlt: "Imagen de notificación",
  },

  conversations: {
    replyAriaLabel: "Responder",
    replyPlaceholder: "Responder…",
    replySend: "Enviar",
    sent: "Enviado",
    failedToSend: "No se pudo enviar",
    dismiss: "Descartar conversación",
    messagesListLabel: "Mensajes en esta conversación",
    deviceOwner: "Tú",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Sin mensajes nuevos"
        : `${count} ${selectPlural("es", count, {
            one: "mensaje sin leer",
            other: "mensajes sin leer",
          })}`,
  },

  list: {
    today: "Hoy",
    yesterday: "Ayer",
    searchNotifications: "Buscar notificaciones",
    clearSearch: "Borrar búsqueda",
    clearAll: "Borrar todo",
    markAllRead: "Marcar todo como leído",
    groupCheckboxLabel: "Agrupar",
    noNotificationsTitle: "Aún no hay notificaciones",
    noNotificationsDesc: "Las notificaciones de tu teléfono aparecerán aquí.",
    noMatchesTitle: "Sin coincidencias",
    notificationsList: "Notificaciones",
    privacyAppList: "Notificaciones por aplicación",
    clear: "Borrar",
    clearMatches: (count: number): string =>
      `Borrar ${count} ${selectPlural("es", count, {
        one: "coincidencia",
        other: "coincidencias",
      })}`,
    noMatchesDesc: (query: string): string => `Ninguna notificación coincide con “${query}”.`,
    appNotificationsGroup: (appName: string): string => `Notificaciones de ${appName}`,
    dismissAllFrom: (appName: string): string => `Descartar todas de ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("es", count, { one: "mensaje", other: "mensajes" })
          : kind === "calls"
            ? selectPlural("es", count, { one: "llamada", other: "llamadas" })
            : selectPlural("es", count, { one: "notificación", other: "notificaciones" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "descartar",
    kbdNavigate: "navegar",
    kbdOrSeparator: "o",
  },

  settings: {
    back: "Volver a notificaciones",
    settings: "Configuración",
    general: "General",
    theme: "Tema",
    themeAuto: "Automático",
    themeLight: "Claro",
    themeDark: "Oscuro",
    language: "Idioma",
    languageSystemDefault: "Predeterminado del sistema",
    notificationsSection: "Notificaciones",
    osNotifications: "Notificaciones del sistema",
    callNotifications: "Notificaciones de llamadas",
    privacySecurity: "Privacidad y seguridad",
    analyticsToggle: "Estadísticas de uso anónimas",
    analyticsToggleDescription: "Solo recuentos e hitos — nunca tus notificaciones.",
    statusConnected: "Conectado",
    statusReconnecting: "Reconectando…",
    statusDisconnected: "Desconectado",
    disconnectFromPhone: "Desconectar del teléfono",
    disconnectConfirm: "¿Estás seguro? Se desvinculará tu dispositivo.",
    yesDisconnect: "Sí, desconectar",
    cancel: "Cancelar",
    version: "Versión",
    encryption: "Cifrado",
    encryptionValue: "E2E (NaCl)",
    needHelp: "¿Necesitas ayuda? Escribe a __EMAIL__",
    disconnectFailed: "No se pudo desconectar — vuelve a intentarlo",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Desconectado, pero la limpieza local falló: ${errorMsg}. Vuelve a cargar la página.`,
    disconnectLocalCleanupFailed:
      "Desconectado, pero la limpieza local falló. Vuelve a cargar la página.",
  },

  pairing: {
    enterCodeInstruction: "Introduce este código en la app Reflecto",
    pairedSuccessfully: "¡Vinculación correcta!",
    pairingFailed: "Vinculación fallida",
    requestingPairingCode: "Solicitando código de vinculación…",
    codeExpired: "Código caducado",
    tryAgain: "Reintentar",
    failedToStartPairing: "No se pudo iniciar la vinculación",
    installHeading: "¿Aún no tienes la app?",
    installScanHint: "Escanéalo con la cámara de tu teléfono",
    installQrLabel: "Código QR para instalar Reflecto en tu teléfono",
    capabilitiesHeading: "Una vez vinculado podrás:",
    capabilityMirror: "Ver aquí las notificaciones de tu teléfono",
    capabilityReply: "Responder sin tocar el teléfono",
    capabilityShare: "Compartir el portapapeles y archivos en ambos sentidos",
    encryptionNote: "Cifrado de extremo a extremo. El servidor nunca ve tu contenido.",
  },

  dnd: {
    title: "No molestar",
    description: "Pausa las notificaciones en este dispositivo durante una hora.",
    snooze1h: "Posponer 1 hora",
    snoozeActiveUntil: (formattedTime: string): string => `Activo hasta ${formattedTime}`,
    cancelSnooze: "Cancelar",
  },

  connectors: {
    title: "Servicios conectados",
    emptyBody:
      "Aún no hay servicios conectados. Conecta ChatGPT o Claude desde su configuración de conectores y luego aprueba el código en tu teléfono.",
    scopeSend: "Puede enviar notificaciones",
    added: (date: string): string => `Añadido ${date}`,
    lastUsed: (relative: string): string => `Usado por última vez ${relative}`,
    neverUsed: "Nunca usado",
    revoke: "Desconectar",
    revokeConfirm: (label: string): string =>
      `¿Desconectar ${label}? Perderá el acceso de inmediato.`,
    revokeAction: "Desconectar",
    revokeConfirmLabel: (label: string): string => `Desconectar ${label}`,
    cancel: "Cancelar",
    loadError: "No se pudieron cargar los servicios conectados. Inténtalo de nuevo.",
    revokeError: "No se pudo desconectar el servicio. Inténtalo de nuevo.",
    retry: "Reintentar",
  },

  safetyNumber: {
    rowLabel: "Verificar cifrado",
    rowSublabel: "Compara números de seguridad con tu teléfono",
    cardTitle: "Número de seguridad",
    caption: (deviceLabel: string): string =>
      `Compara con el número que se muestra en ${deviceLabel}. Si ambos coinciden, tu cifrado de extremo a extremo está intacto.`,
    repairNote: "Este número cambia cuando vuelves a vincular un dispositivo.",
    copy: "Copiar",
    copied: "Copiado",
    maskedHint: "Oculto mientras el modo de privacidad está activado",
  },

  feedback: {
    title: "Enviar comentarios",
    close: "Cerrar",
    messageLabel: "Tu mensaje",
    messagePlaceholder: "¿Encontraste un error, tienes una idea o algo no te cuadra? Cuéntanos.",
    categoryLabel: "Categoría",
    categoryBug: "Error",
    categoryIdea: "Idea",
    categoryOther: "Otro",
    emailLabel: "Correo (opcional, solo si quieres una respuesta)",
    emailPlaceholder: "tu@ejemplo.com",
    emailHelp: "No te añadiremos a ninguna lista.",
    disclosure:
      "Tu mensaje se envía al servidor de Reflecto y se registra en nuestro sistema interno de seguimiento de incidencias. Adjuntamos la versión de la app y un ID de dispositivo asignado durante el vinculación para poder correlacionar los reportes. No vemos tus notificaciones, contactos ni ningún dato personal. El correo es opcional — facilítalo solo si quieres una respuesta. No te añadimos a ninguna lista.",
    submit: "Enviar comentarios",
    sending: "Enviando…",
    success: "Gracias — lo lee una persona, normalmente en menos de 24 h",
    error: "No se pudo enviar. Inténtalo de nuevo.",
    done: "Listo",
    // Etiqueta del punto de entrada en Ajustes (abre el modal). Idéntica a propósito
    // a `submit` (el botón del modal) — claves distintas para que cada idioma pueda divergir.
    sendFeedback: "Enviar comentarios",
  },

  uninstall: {
    headline: "La extensión de Reflecto está desinstalada",
    body: "Nunca hubo una cuenta, y la extensión guardaba tu vinculación en este equipo, así que no queda nada que tengas que limpiar.",
    ask: "Si tienes treinta segundos, nos gustaría saber qué salió mal.",
    categoryLegend: "¿Qué tipo de comentario es?",
    categoryBug: "Algo se rompió",
    categoryIdea: "Le faltaba algo que necesitaba",
    categoryOther: "Otra cosa",
    messageLabel: "¿Qué salió mal?",
    messagePlaceholder: "Las notificaciones dejaron de llegar al cabo de unas horas.",
    messageRequired: "Escribe una frase primero y luego envía.",
    charactersLeft: (count: number): string => `Quedan ${count} caracteres`,
    emailLabel: "Correo (opcional)",
    emailPlaceholder: "tu@ejemplo.com",
    emailHelp: "Solo se usa para responderte. Nada más.",
    submit: "Enviar comentarios",
    sending: "Enviando…",
    retry: "Reintentar",
    privacyNote:
      "Este formulario no está vinculado a ningún dispositivo. No contiene ninguna de tus notificaciones, ni tu portapapeles, ni tus archivos. El correo es opcional y solo se usa para responder.",
    success: "Gracias. Una persona lee cada uno de estos mensajes.",
    errorGeneric: "No se envió. Tu mensaje sigue aquí.",
    errorRateLimited:
      "Has enviado varios mensajes hace poco. Inténtalo de nuevo dentro de una hora aproximadamente. Tu texto se queda aquí.",
    footerWebApp: "La app web de Reflecto sigue funcionando sin la extensión.",
    footerWebAppLink: "Abrir la app web",
  },

  glance: {
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Cerrar",
    aiSummaryTag: "Resumen con IA",
    errorAi: "No se pudo generar un resumen en este momento.",
    retryCta: "Reintentar",
    // Tarjeta de incorporación cuando el modelo en el dispositivo se puede descargar.
    downloadHeading: "Obtén resúmenes con IA",
    downloadBody:
      "Ponte al día con lo que te perdiste con un resumen de IA en el dispositivo. Se ejecuta totalmente en tu dispositivo.",
    downloadCta: "Descargar modelo",
    downloadPrivacyNote: "Una descarga única. El servidor nunca ve tus notificaciones.",
    downloadingHeading: "Descargando modelo…",
    caughtUp: "Estás al día: nada requiere tu atención ahora mismo.",
    needsYou: "Te necesita",
    quiet: "Tranquilo",
    scanning: (pending: number): string => `Resumiendo ${pending}…`,
    jump: "Abrir notificación",
    affordanceReply: "Responder",
    affordanceOpen: "Abrir",
    affordanceCopyOtp: "Copiar código",
    buttonLabel: "Glance",
  },

  update: {
    title: "Actualización disponible",
    description: "Una nueva versión de Reflecto está lista. Recarga para actualizar.",
    reload: "Recargar",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Llamamos a este dispositivo “${name}”.`,
    cardLooksGood: "Está bien",
    cardEdit: "Editar",
    cardAriaLabel: "Confirmar nombre del dispositivo",
    renameTitle: "Cambiar nombre del dispositivo",
    renameLabel: "Nombre del dispositivo",
    renameSave: "Guardar",
    renameCancel: "Cancelar",
    renameFailed: "No se pudo renombrar. Inténtalo de nuevo.",
  },

  collision: {
    title: (name: string): string => `Ya tienes un “${name}”`,
    subtitle: "¿Cómo deberíamos llamar a este?",
    chipWork: (name: string): string => `${name} del trabajo`,
    chipHome: (name: string): string => `${name} del hogar`,
    chipPersonal: (name: string): string => `${name} personal`,
    customLabel: "Nombre personalizado",
    save: "Guardar",
    useAnyway: "Usar el nombre original de todos modos",
    saveFailed: "No se pudo guardar el nuevo nombre. Inténtalo de nuevo.",
    ariaLabel: "Resolver nombre duplicado del dispositivo",
  },

  clipboard: {
    title: "Portapapeles",
    emptyTitle: "Aún no hay clips",
    empty: "Los clips de tus otros dispositivos aparecen aquí. Toca para copiar.",
    from: (label: string): string => `de ${label}`,
    to: (label: string): string => `a ${label}`,
    copy: "Copiar al portapapeles",
    copied: "Copiado",
    reveal: "Mostrar",
    hide: "Ocultar",
    maskedHint: "Contenido oculto: pulsa Mostrar para verlo",
    sensitiveChip: "Sensible",
    cloudChip: "desde tu token de API",
    delete: "Eliminar clip",
    sendPlaceholder: "Pega texto para enviar…",
    sendCta: "Enviar",
    sendSensitiveToggle: "Marcar como sensible",
    sendSuccess: (n: number): string =>
      `Enviado a ${n} ${selectPlural("es", n, { one: "dispositivo", other: "dispositivos" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Llegó a ${n}/${total} ${selectPlural("es", total, {
        one: "dispositivo",
        other: "dispositivos",
      })}.`,
    sendNoDevices: "No hay dispositivos vinculados para enviar.",
    sendFailed: "No se pudo enviar. Inténtalo de nuevo.",
    sendEmpty: "Nada que enviar.",
    dockRecentHeading: "Recientes",
    dockEmptyClipboard: "Tu portapapeles está vacío.",
    clearAll: "Borrar todo",
    clearAllConfirm: "Sí, borrar todo",
    clearAllCancel: "Cancelar",
    shareTitle: "Enviar a tus dispositivos",
    shareReview:
      "Revisa lo que vas a enviar. Va a tus dispositivos vinculados, cifrado de extremo a extremo.",
    shareSend: "Enviar",
    shareCancel: "Cancelar",
    shareNotPaired: "Vincula un dispositivo primero para enviar texto compartido.",
    shareOpenApp: "Abrir Reflecto",
    pasteFromClipboard: "Pegar desde el portapapeles",
    readFailed: "No se pudo leer el portapapeles: pega el texto abajo.",
  },
  install: {
    title: "Instalar Reflecto",
    body: "Agrégalo a tu pantalla de inicio para acceder más rápido.",
    cta: "Instalar",
    dismissAria: "Descartar el aviso de instalación",
  },
  nav: {
    home: "Inicio",
    clipboard: "Portapapeles",
    files: "Archivos",
    ariaLabel: "Principal",
    fileHiddenPublicMode: "Oculto mientras el modo privado está activado.",
  },
  common: {
    loading: "Cargando",
  },
  errors: {
    notPaired: "Sin vincular",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} en ${os}`,
    webFallback: "Navegador web",
  },
  palette: {
    placeholder: "Escribe un comando…",
    noResults: "No hay comandos coincidentes",
    ariaLabel: "Paleta de comandos",
    clearAll: "Borrar todas las notificaciones",
    glance: "Resumen de Glance",
    cancelSnooze: "Cancelar posposición",
    copyClip: "Copiar el último clip recibido",
  },

  fileTransfer: {
    sendTitle: "Enviar un archivo",
    sendChooseFile: "Elegir archivo",
    sendDeviceLabel: "Enviar a",
    sendPhoneFallbackName: "Teléfono",
    send: "Enviar archivo",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Preparando…" : `Preparando — ${pct}%`,
    sendDelivering: "Entregando a tu teléfono…",
    sendWaitingAccept: "Esperando que tu teléfono acepte…",
    sendWaitingAppOpen: "Abre Reflecto en tu teléfono para aceptar",
    sendWaitingAppOpenHint:
      "Tu teléfono no puede recibir notificaciones push, así que no mostrará este archivo hasta que abras Reflecto.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Subiendo parte ${partNumber} de ${partCount}…`,
    sendDone: "Enviado.",
    sendFailedRejected: "Tu teléfono rechazó el archivo.",
    sendFailedNoAnswer: "Tu teléfono no respondió.",
    sendFailedUploadFailed: "Error al subir. Inténtalo de nuevo.",
    sendFailedCancelled: "Cancelado.",
    sendFailedGeneric: "Algo salió mal. Inténtalo de nuevo.",
    sendTooLarge: (maxMiB: number): string =>
      `El archivo es demasiado grande (máx. ${maxMiB} MiB).`,
    sendKeepOpenHint: "Mantén esta pestaña abierta hasta que termine la transferencia.",
    sendAnother: "Enviar otro",
    sendNoDevice: "Vincula un teléfono primero para enviar archivos.",
    sendCancel: "Cancelar",
    sendPeerProgress: (chunksDone: number): string =>
      `El teléfono ha recibido ${chunksDone} fragmentos.`,

    receiveTitle: "Archivos entrantes",
    receiveOfferFrom: (sender: string): string => `${sender} quiere enviarte un archivo`,
    receiveAccept: "Aceptar",
    receiveDecline: "Rechazar",
    receiveReceiving: (n: number, total: number): string => `Recibiendo parte ${n} de ${total}…`,
    receiveDone: "Guardado.",
    receiveFailed: "La transferencia falló.",
    receiveCancelled: "Cancelado.",
    receiveCancel: "Cancelar",

    errorChunkAuthFail:
      "Error en la transferencia — no se pudieron verificar los datos. Vuelve a intentarlo.",
    errorHashMismatch:
      "Error en la transferencia — el archivo no superó la verificación de integridad. Vuelve a intentarlo.",
    errorStorageFull: "No hay suficiente espacio de almacenamiento para esta transferencia.",
    errorExpired: "La transferencia ha expirado.",
    errorQuotaExceeded:
      "Se alcanzó el límite diario de transferencias — inténtalo de nuevo mañana.",
    errorInternal: "Error en la transferencia — algo salió mal.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct} %`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Descartar",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Progreso de la transferencia",
    outgoing: (n: number): string => `Salientes (${n})`,
    incoming: (n: number): string => `Entrantes (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Aceptar todo",
    dropTitle: "Arrastra y suelta aquí",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Archivos de hasta ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Arrastra y suelta aquí, o elige un archivo para enviar",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Directo",
    transportDirectHint:
      "Enviado de dispositivo a dispositivo: nunca se almacena en nuestros servidores.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Vía servidor",
    transportRelayHint:
      "Retransmitido por nuestros servidores, sigue cifrado de extremo a extremo: no podemos leerlo.",
    directBadge: "Directo",
  },
  time: {
    justNow: "justo ahora",
    minutesAgo: (count: number): string => `hace ${count} min`,
    hoursAgo: (count: number): string => `hace ${count} h`,
  },
} satisfies MessagesShape;
